import type {
  ComponentOrReferenceFormattingRule,
  FormattingRule,
} from '@ardoq/data-model';
import {
  APIEntityType,
  APIFieldType,
  FilterInfoOperator,
  FormattingRuleOperator,
  ListFieldFormatting,
} from '@ardoq/api-types';
import {
  DateComparatorFamily,
  getDateComparatorGroups,
  getNumberComparatorGroups,
  QueryBuilderFilterType,
} from '@ardoq/query-builder';
import { _ComponentDefaultFilterKeys } from '@ardoq/api-types';
import type { FormattingRuleInfo } from '../../types';
import type { Formatting, FormattingGroup } from './types';
import { buildColorWarningsMap } from './colorWarnings';
import { buildNumberWarningsMap } from './numberWarnings';
import type { I18nLocale } from '@ardoq/i18n';

const isComponentOrReferenceRule = (
  rule: FormattingRule
): rule is ComponentOrReferenceFormattingRule =>
  rule.type !== APIEntityType.TAG;

/**
 * augments the legacy formatting comparators with more UX-friendly ones.
 *
 * the legacy comparators are functional, but are incomplete for providing a user interface.
 *
 * our augmented comparators are concepts to present to the user in the UI. these can be enhanced or simplified versions of what we can send to various filter-operating code.
 *
 * @returns formatting comparators which describe a better user interface for formatting rules.
 */
const augmentFormattingComparators = (
  isNewConditionalFormatting: boolean,
  formatting?: Formatting
) => {
  if (!formatting) {
    return [];
  }
  const { filterType, comparators, id } = formatting;
  if (!isNewConditionalFormatting) {
    return comparators;
  }
  switch (filterType) {
    case QueryBuilderFilterType.NUMBER: {
      const cannotBeEmpty =
        id === _ComponentDefaultFilterKeys.VERSION ||
        id === _ComponentDefaultFilterKeys.INCOMING_REF_COUNT ||
        id === _ComponentDefaultFilterKeys.OUTGOING_REF_COUNT;

      // number fields now have a "color range" comparator. this option appears in a separate group before the standard number operators.
      return getNumberComparatorGroups({ cannotBeEmpty });
    }
    case QueryBuilderFilterType.ASYNC_COMPONENT_OID:
    case QueryBuilderFilterType.ASYNC_REFERENCE_OID:
    case QueryBuilderFilterType.ASYNC_PARENT:
    case QueryBuilderFilterType.ASYNC_SOURCE:
    case QueryBuilderFilterType.ASYNC_TARGET:
    case QueryBuilderFilterType.ASYNC_ROOT_WORKSPACE:
    case QueryBuilderFilterType.SELECT: {
      // expected comparators from input: [id_match]. we need a does-not-match comparator because the "inverse" option is being removed from the app.
      const isIdMatchSelector =
        comparators.length === 1 &&
        comparators[0] === FilterInfoOperator.ID_MATCH;
      if (isIdMatchSelector) {
        return [
          FilterInfoOperator.ID_MATCH,
          FormattingRuleOperator.ID_DOES_NOT_MATCH,
        ];
      }
      break;
    }
    case QueryBuilderFilterType.SELECT_MULTIPLE:
      // expected comparators from input: [equals, contains]
      return [
        FilterInfoOperator.EQUALS,
        FormattingRuleOperator.NOT_EQUAL,
        FormattingRuleOperator.LIST_CONTAINS,
        FormattingRuleOperator.LIST_DOES_NOT_CONTAIN,
        FormattingRuleOperator.IS_EMPTY,
      ];
    case QueryBuilderFilterType.STRING:
      // expected comparators from input: [equals, contains, greater than, less than]. prefer the following comparators, which are how users prefer to work with string fields.
      return [
        FilterInfoOperator.EQUALS,
        FormattingRuleOperator.NOT_EQUAL,
        FilterInfoOperator.CONTAINS,
        FormattingRuleOperator.NOT_CONTAINS,
        FormattingRuleOperator.IS_EMPTY,
      ];
    case QueryBuilderFilterType.ASYNC_COMPONENT_NAME: {
      const isNameSelector = id === _ComponentDefaultFilterKeys.NAME;
      if (isNameSelector) {
        return [
          FilterInfoOperator.EQUALS,
          FormattingRuleOperator.NOT_EQUAL,
          FilterInfoOperator.CONTAINS,
          FormattingRuleOperator.NOT_CONTAINS,
        ];
      }
      break;
    }
    case QueryBuilderFilterType.BOOLEAN:
      // expected comparators from input: [boolean]. we need to add the negatory operator since the "Invert" checkbox is being removed.
      return [
        FilterInfoOperator.BOOLEAN,
        FormattingRuleOperator.NOT_BOOLEAN,
        FormattingRuleOperator.IS_EMPTY,
      ];
    case QueryBuilderFilterType.ASYNC_USER:
      return [
        FilterInfoOperator.EQUALS,
        FormattingRuleOperator.NOT_EQUAL,
        FormattingRuleOperator.IS_EMPTY,
      ];
    case QueryBuilderFilterType.DATE:
    case QueryBuilderFilterType.DATE_TIME:
      return getDateComparatorGroups(DateComparatorFamily.DATE);
    case QueryBuilderFilterType.DATE_RANGE:
      return getDateComparatorGroups(DateComparatorFamily.DATE_RANGE);
  }
  return comparators;
};

const mapFormattingRuleInfo = (
  i18nLocale: I18nLocale,
  conditionalFormattingRules: FormattingRule[],
  filterGroupsByRuleId: Partial<Record<string, FormattingGroup[]>>,
  formattingByRuleAndField: Record<string, Partial<Record<string, Formatting>>>,
  listFieldFormattingByRuleId: Partial<Record<string, ListFieldFormatting>>,
  isNewConditionalFormatting: boolean,
  fieldTypesByName: Record<string, APIFieldType>
) => {
  const colorWarningsByKey = isNewConditionalFormatting
    ? buildColorWarningsMap(
        i18nLocale,
        conditionalFormattingRules,
        formattingByRuleAndField
      )
    : {};

  const numberWarningsByKey = isNewConditionalFormatting
    ? buildNumberWarningsMap(
        i18nLocale,
        fieldTypesByName,
        conditionalFormattingRules,
        formattingByRuleAndField
      )
    : {};

  return Object.fromEntries(
    conditionalFormattingRules.map((rule): [string, FormattingRuleInfo] => {
      const { __id: ruleId } = rule;

      return [
        ruleId,
        isComponentOrReferenceRule(rule)
          ? {
              filterGroups: filterGroupsByRuleId[ruleId],
              fieldFormatting: formattingByRuleAndField[ruleId],
              listFieldFormatting: listFieldFormattingByRuleId[ruleId],
              comparators: rule.field
                ? augmentFormattingComparators(
                    isNewConditionalFormatting,
                    formattingByRuleAndField[ruleId]?.[rule.field]
                  )
                : [],
              colorWarnings: colorWarningsByKey[ruleId] || {},
              numberWarnings: numberWarningsByKey[ruleId] || {},
            }
          : {
              filterGroups: undefined,
              fieldFormatting: {},
              listFieldFormatting: undefined,
              comparators: [],
              colorWarnings: colorWarningsByKey[ruleId] || {},
            },
      ];
    })
  );
};

export { mapFormattingRuleInfo, augmentFormattingComparators };
